import React, { useEffect, useRef, useState } from 'react';
import '../../css/RTSPViewer.css';
import {
  WEB_RTC_TURN_PSWD,
  WEB_RTC_TURN_USR,
  AUTHREQ,
} from '../../constants/constants';
import { useRobotDetails } from '../../utilities/RobotDetailsContext';
import { CameraStreams } from '../../components/Pages/HomePage';

interface WebRTCVideoProps {
  server: string;
  src: CameraStreams;
}

const MAX_RETRIES = 3;
const CONNECTION_TIMEOUT = 10000;

const WebRTCVideo: React.FC<WebRTCVideoProps> = ({
  server,
  src,
}) => {
  const { robotDetails } = useRobotDetails();

  const videoRef = useRef<HTMLVideoElement>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const retryCountRef = useRef(0);

  // Each connection gets a unique ID. Any event belonging to
  // an old connection is ignored.
  const connectionIdRef = useRef(0);

  const pendingCandidatesRef = useRef<string[]>([]);

  const [connected, setConnected] = useState(false);

  const pcConfig: RTCConfiguration = {
    bundlePolicy: 'max-bundle',

    iceServers: [
      {
        urls: 'stun:stun.l.google.com:19302',
      },
      {
        urls: 'turns:turn.wrybillrobotics.com:3478',
        username: WEB_RTC_TURN_USR,
        credential: WEB_RTC_TURN_PSWD,
      },
      {
        urls: 'turn:turn.wrybillrobotics.com:3478',
        username: WEB_RTC_TURN_USR,
        credential: WEB_RTC_TURN_PSWD,
      },
    ],

    iceCandidatePoolSize: 10,
  };

  useEffect(() => {
    let destroyed = false;

    const clearTimeouts = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const cleanup = () => {
      clearTimeouts();

      // Invalidate all existing callbacks.
      connectionIdRef.current += 1;

      // Close WebSocket first.
      const ws = wsRef.current;
      wsRef.current = null;

      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;

          if (
            ws.readyState === WebSocket.OPEN ||
            ws.readyState === WebSocket.CONNECTING
          ) {
            ws.close();
          }
        } catch (error) {
          console.warn('Error closing WebSocket:', error);
        }
      }

      // Then close WebRTC.
      const pc = pcRef.current;
      pcRef.current = null;

      if (pc) {
        try {
          pc.onicecandidate = null;
          pc.ontrack = null;
          pc.onconnectionstatechange = null;
          pc.oniceconnectionstatechange = null;

          if (pc.signalingState !== 'closed') {
            pc.close();
          }
        } catch (error) {
          console.warn(
            'Error closing RTCPeerConnection:',
            error
          );
        }
      }

      pendingCandidatesRef.current = [];

      // Release media resources.
      if (videoRef.current) {
        const stream = videoRef.current
          .srcObject as MediaStream | null;

        if (stream) {
          stream.getTracks().forEach(track => {
            try {
              track.stop();
            } catch {
              // Ignore already-stopped tracks.
            }
          });
        }

        videoRef.current.srcObject = null;
      }

      setConnected(false);
    };

    const connect = async (attempt: number) => {
      if (destroyed || src === 'none') {
        return;
      }

      // Always start from a clean state.
      cleanup();

      const connectionId = connectionIdRef.current;

      const isCurrent = () =>
        !destroyed &&
        connectionId === connectionIdRef.current;

      console.log(
        `Starting WebRTC connection ` +
          `(attempt ${attempt}/${MAX_RETRIES + 1})`
      );

      const pc = new RTCPeerConnection(pcConfig);
      pcRef.current = pc;

      pc.addTransceiver('video', {
        direction: 'recvonly',
      });

      pc.addTransceiver('audio', {
        direction: 'recvonly',
      });

      pc.ontrack = event => {
        if (!isCurrent() || !videoRef.current) {
          return;
        }

        let stream = videoRef.current
          .srcObject as MediaStream | null;

        if (!stream) {
          stream = new MediaStream();
          videoRef.current.srcObject = stream;
        }

        if (
          !stream
            .getTracks()
            .some(track => track.id === event.track.id)
        ) {
          stream.addTrack(event.track);
        }

        videoRef.current.play().catch(() => {
          // Browser autoplay restrictions.
        });
      };

      pc.onicecandidate = event => {
        if (!isCurrent() || !event.candidate) {
          return;
        }

        const ws = wsRef.current;

        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              type: 'webrtc/candidate',
              value: event.candidate.candidate,
            })
          );
        }
      };

      pc.onconnectionstatechange = () => {
        if (!isCurrent()) {
          return;
        }

        console.log(
          'WebRTC state:',
          pc.connectionState
        );

        switch (pc.connectionState) {
          case 'connected':
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }

            retryCountRef.current = 0;
            setConnected(true);
            break;

          case 'failed':
            setConnected(false);

            if (attempt <= MAX_RETRIES) {
              cleanup();

              retryTimeoutRef.current = setTimeout(() => {
                connect(attempt + 1);
              }, 1000 * attempt);
            }
            break;

          case 'disconnected':
            setConnected(false);
            break;

          case 'closed':
            setConnected(false);
            break;

          default:
            setConnected(false);
            break;
        }
      };

      const wsProtocol = server.startsWith('https')
        ? 'wss'
        : 'ws';

      const serverAddress = server.replace(
        /^(http|https):\/\//,
        ''
      );

      const cameraSource =
        AUTHREQ === false
          ? src
          : `${robotDetails.robotName}_${src}`;

      const wsUrl =
        `${wsProtocol}://${serverAddress}` +
        `/api/ws?src=${cameraSource}`;

      console.log('Opening WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        if (!isCurrent()) {
          ws.close();
          return;
        }

        try {
          const offer = await pc.createOffer();

          if (!isCurrent()) {
            return;
          }

          await pc.setLocalDescription(offer);

          if (
            !pc.localDescription ||
            ws.readyState !== WebSocket.OPEN ||
            !isCurrent()
          ) {
            return;
          }

          ws.send(
            JSON.stringify({
              type: 'webrtc/offer',
              value: pc.localDescription.sdp,
            })
          );
        } catch (error) {
          console.error(
            'Error creating WebRTC offer:',
            error
          );
        }
      };

      ws.onmessage = async event => {
        if (!isCurrent()) {
          return;
        }

        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'webrtc/answer') {
            await pc.setRemoteDescription(
              new RTCSessionDescription({
                type: 'answer',
                sdp: msg.value,
              })
            );

            // Apply candidates that arrived before the answer.
            const pending = pendingCandidatesRef.current;
            pendingCandidatesRef.current = [];

            for (const candidate of pending) {
              try {
                await pc.addIceCandidate(
                  new RTCIceCandidate({
                    candidate,
                    sdpMid: '0',
                  })
                );
              } catch (error) {
                console.error(
                  'Error adding queued ICE candidate:',
                  error
                );
              }
            }
          }

          if (msg.type === 'webrtc/candidate') {
            if (!pc.remoteDescription) {
              pendingCandidatesRef.current.push(
                msg.value
              );
            } else {
              await pc.addIceCandidate(
                new RTCIceCandidate({
                  candidate: msg.value,
                  sdpMid: '0',
                })
              );
            }
          }
        } catch (error) {
          console.error(
            'Error processing WebRTC message:',
            error
          );
        }
      };

      ws.onerror = error => {
        if (!isCurrent()) {
          return;
        }

        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        if (!isCurrent()) {
          return;
        }

        console.log('WebSocket closed');
        setConnected(false);

        if (
          pc.connectionState !== 'connected' &&
          attempt <= MAX_RETRIES
        ) {
          cleanup();

          retryTimeoutRef.current = setTimeout(() => {
            connect(attempt + 1);
          }, 1000 * attempt);
        }
      };

      // Don't allow a dead connection to sit around forever.
      timeoutRef.current = setTimeout(() => {
        if (
          isCurrent() &&
          pc.connectionState !== 'connected'
        ) {
          console.warn(
            'WebRTC connection timed out'
          );

          cleanup();

          if (attempt <= MAX_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              connect(attempt + 1);
            }, 1000 * attempt);
          }
        }
      }, CONNECTION_TIMEOUT);
    };

    if (src !== 'none') {
      connect(1);
    } else {
      cleanup();
    }

    return () => {
      destroyed = true;
      cleanup();
    };
  }, [
    server,
    src,
    robotDetails.robotName,
  ]);

  return (
    <div className="fullscreen-video">
      <video
        id="video"
        ref={videoRef}
        autoPlay
        controls={!connected}
        muted
        playsInline
      />
    </div>
  );
};

export default WebRTCVideo;